import { Button, tokens } from "@blurb/codex-react";
import { createStyles } from "@mantine/core";
import { IconV3 } from "@rpi/openapi-core";
import { UseRpiPaginationResult } from "../hooks/usePagination";

const usePaginationStyles = createStyles({
  wrapper: {
    display: "flex",
    flex: "auto",
    justifyContent: "space-between",
    alignItems: "center",
    padding: `${tokens.spacing[2]} ${tokens.spacing[4]}`,

    color: tokens.color.semantic.text.subtle,
    fontSize: tokens.font.size.sm,
    "font-weight": tokens.font.weight.normal,
    lineHeight: tokens.font.lineHeight.normal,
  },
  carousel: {
    display: "flex",
    flexDirection: "row",
    gap: tokens.spacing[2],
    alignItems: "center",
  },
  ellipsis: {
    width: "32px",
    height: "32px",
    fontSize: tokens.font.size.base,
    textAlign: "center",
    alignContent: "end",
  },
  button: {
    padding: 0,
    textDecoration: "none",
  },
  pageButton: {
    width: "32px",
    height: "32px",
    "&[data-current-page=true]": {
      color: tokens.color.semantic.text.brand,
      backgroundColor: tokens.color.semantic.bg.brandSubtlest,
      borderRadius: tokens.radius.lg,
    },
  },
  navButton: {
    color: tokens.color.semantic.icon.bold,
    "&:disabled": {
      color: tokens.color.semantic.icon.disabled,
    },
  },
});

interface PaginationProps {
  pagination: UseRpiPaginationResult;
  isLoading?: boolean;
}

const CAROUSEL_MAX = 6;
const CAROUSEL_MIDDLE = 3;

export function PaginationV2({ pagination, isLoading }: PaginationProps) {
  const { classes, cx } = usePaginationStyles();
  const pageNumberButton = cx(classes.button, classes.pageButton);
  const navButton = cx(classes.button, classes.navButton);
  if (pagination.pages === 0) {
    return null;
  }

  let carouselCount = pagination.pages > CAROUSEL_MAX ? CAROUSEL_MAX - 1 : pagination.pages;
  let carouselOffset = 0;

  const hasPreviousPages = pagination.page >= carouselCount;
  const hasNextPages =
    pagination.pages > CAROUSEL_MAX &&
    pagination.page < pagination.pages - Math.min(carouselCount, pagination.pages - CAROUSEL_MAX + 1);
  const isMiddle = hasPreviousPages && hasNextPages;

  if (isMiddle) {
    carouselCount = CAROUSEL_MIDDLE;
    carouselOffset = pagination.page - 1;
  } else if (hasPreviousPages) {
    carouselOffset = pagination.pages - carouselCount;
  }

  const carouselPages = Array(carouselCount)
    .fill(carouselOffset)
    .map((offset, i) => offset + i);

  return (
    <div className={classes.wrapper}>
      {`Showing ${pagination.page * pagination.perPage + 1}-${Math.min(
        pagination.page * pagination.perPage + pagination.perPage,
        pagination.totalCount
      )} of ${pagination.totalCount} Products`}

      <div className={classes.carousel}>
        <Button
          key="paginate-back"
          variant="text"
          intent="neutral"
          className={navButton}
          onClick={pagination.previousPage}
          disabled={isLoading || pagination.page === 0}
        >
          <IconV3 icon="chevron_left" color="inherit" size={24} />
        </Button>
        {hasPreviousPages && (
          <>
            <Button
              key="first-page"
              className={pageNumberButton}
              variant="text"
              intent="neutral"
              onClick={pagination.firstPage}
              disabled={isLoading}
            >
              {1}
            </Button>
            <span className={classes.ellipsis}>...</span>
          </>
        )}
        {carouselPages.map((pageIndex) => (
          <Button
            key={pageIndex}
            className={pageNumberButton}
            variant="text"
            intent="neutral"
            onClick={() => pagination.setPage(pageIndex)}
            disabled={isLoading || pagination.page === pageIndex}
            data-current-page={pagination.page === pageIndex}
          >
            {pageIndex + 1}
          </Button>
        ))}
        {hasNextPages && (
          <>
            <span className={classes.ellipsis}>...</span>
            <Button
              key="last-page"
              className={pageNumberButton}
              variant="text"
              intent="neutral"
              onClick={pagination.lastPage}
              disabled={isLoading}
            >
              {pagination.pages}
            </Button>
          </>
        )}
        <Button
          key="paginate-forward"
          variant="text"
          intent="neutral"
          className={navButton}
          onClick={pagination.nextPage}
          disabled={isLoading || pagination.page === pagination.pages - 1}
        >
          <IconV3 icon="chevron_right" color="inherit" size={24} />
        </Button>
      </div>
    </div>
  );
}
